/**
 * The **Quick Tech menu** — Quick Tech modelled as a
 * **chooser** over the four RAW built-in tech options {Bolster, Invade, Lock On, Scan}, each
 * routing to its **existing** resolver. Quick Tech (Core p. 67) is one Quick action that lets
 * a mech "Lock On, Invade, or use a Quick Tech option" — RAW frames it as *choose one*, so this
 * is a sub-picker (mirroring the Skirmish/Barrage arm-then-pick flow), NOT four discrete actions.
 *
 * This is the **engine/intent model**: a data table of the options + a pure router that maps a
 * chosen option to the intent its existing resolver already consumes — nothing here rebuilds
 * Invade / Lock On / Scan / Bolster, whose resolvers already exist:
 *
 *   - **Bolster** → `request_activate { action: "act_bolster" }` → {@link "./resolve".resolveActivate}
 *   - **Invade** → `request_attack { tech: true, weapon: "act_invade" }` → {@link "./resolve".resolve}
 *   - **Lock On** → `request_activate { action: "act_lock_on" }` → {@link "./resolve".resolveActivate}
 *   - **Scan** → `request_activate { action: "act_scan" }` → {@link "./resolve".resolveActivate}, whose
 *     `reveal` atom sets the target's `disclosure = "full"` (a real mechanical reveal)
 *
 * **The legality seam** ({@link legalQuickTechOptions}) is shared with the NPC tech path: RAW, a
 * **Biological** NPC is immune to tech *other than* Scan and Lock On (Core p. 282), and a **Shut
 * Down** mech is immune to ALL tech, no exceptions (Core p. 71). Each option still declares the
 * static {@link QuickTechOption.allowedVsTechImmune} flag (matching Biological's except-list, for
 * a cheap display-only hint), but the chooser's real filter takes a caller-supplied **per-action
 * predicate** ({@link QuickTechLegalityCtx.targetTechBlocked}) — folded off the target's overlay
 * immunity scopes (rules' `immuneToTechAction`) — because one all-or-nothing boolean can't
 * distinguish Shut Down's no-exceptions block from Biological's except-list. The room's hard gate
 * at `resolveIntent` is the enforcement; this chooser filter keeps the menu honest before a player
 * even submits an intent. **Deferred (the menu offers the four built-ins only):** enumerating a
 * mech's loadout for system-granted Quick Tech options, and Quick vs. status-Quick disclosure.
 *
 * **Pure** — no I/O, no React, no resolver call. Returns the intent object; the CLI / surface
 * sends it and the authority resolves it through the *same* path the direct verbs use.
 */

import type { RequestActivate, RequestAttack } from "@lancer-vtt/shared";

/**
 * How a Quick Tech option resolves — which existing resolver the menu routes to.
 * - `activate` → a `request_activate` over the named overlay action ({@link "./resolve".resolveActivate}).
 * - `tech_attack` → a tech `request_attack` (d20 + Tech Attack vs E-DEFENSE) over the named weapon id.
 */
export type QuickTechRoute = { kind: "activate"; action: string } | { kind: "tech_attack"; weapon: string };

/** One Quick Tech menu option: a stable id, its RAW name, its routing, and the tech-immune legality flag. */
export type QuickTechOption = {
  /** Stable menu key (the option id the chooser surfaces; also the CLI positional). */
  id: string;
  /** The RAW option name as printed (e.g. "Lock On"). */
  name: string;
  /** Which existing resolver this option routes to, and the entity id it carries. */
  route: QuickTechRoute;
  /**
   * Legality seam (shared with the NPC tech path): is this option still offered against a
   * **tech-immune** (Biological) target? RAW: only Scan and Lock On bypass the immunity.
   */
  allowedVsTechImmune: boolean;
};

/**
 * The four RAW Quick Tech built-ins, in the RAW menu order (Lock On / Invade are the named
 * Quick Tech actions, Bolster + Scan are the universal Quick Tech options). Each routes to the
 * resolver that already handles it — this table is the single place the menu→resolver mapping lives.
 */
export const QUICK_TECH_OPTIONS: readonly QuickTechOption[] = [
  // Scan & Lock On are the two options a Biological target is NOT immune to (allowedVsTechImmune).
  { id: "scan", name: "Scan", route: { kind: "activate", action: "act_scan" }, allowedVsTechImmune: true },
  { id: "lock_on", name: "Lock On", route: { kind: "activate", action: "act_lock_on" }, allowedVsTechImmune: true },
  { id: "invade", name: "Invade", route: { kind: "tech_attack", weapon: "act_invade" }, allowedVsTechImmune: false },
  { id: "bolster", name: "Bolster", route: { kind: "activate", action: "act_bolster" }, allowedVsTechImmune: false },
];

/** Look up a Quick Tech option by its menu id; `undefined` for an unknown id (the caller rejects). */
export function quickTechOption(id: string): QuickTechOption | undefined {
  return QUICK_TECH_OPTIONS.find((o) => o.id === id);
}

/** The legality inputs for {@link legalQuickTechOptions}. The seam the NPC tech path will populate. */
export type QuickTechLegalityCtx = {
  /**
   * A **folded per-action tech-immunity predicate** over the current target: `true` iff the
   * target rejects `actionId` (a Shut Down carrier blocks every action id; a Biological carrier
   * excepts `act_scan`/`act_lock_on`). Per-action rather than a single boolean because an
   * all-or-nothing flag can't distinguish Shut Down's no-exceptions immunity from Biological's
   * except-list. A caller folds the target's overlay immunity (rules' `immuneToTechAction`) into
   * this closure and passes it in; `legalQuickTechOptions` itself stays pure/state-free. Absent →
   * no target-immunity filtering (no target picked yet, or an untargeted menu render).
   */
  targetTechBlocked?: (actionId: string) => boolean;
  /**
   * The **acting** combatant is an NPC. RAW narrows an NPC's own Quick
   * Tech menu to Invade **or** Lock On (Core p.280) — Bolster and Scan are PC-universal options an NPC
   * base statblock doesn't carry (a profile's OWN Quick Tech option arrives via the registry's
   * granted path, never through this built-in menu). This is
   * a DIFFERENT axis from `targetTechBlocked` (which narrows on the target's trait, not the actor's kind);
   * the two combine (an NPC actor vs. a Biological target still only offers Lock On, the intersection).
   */
  npcActor?: boolean;
};

/** The action id a Quick Tech option's route resolves as — what `targetTechBlocked` is keyed on. */
function optionActionId(option: QuickTechOption): string {
  return option.route.kind === "tech_attack" ? option.route.weapon : option.route.action;
}

/**
 * The Quick Tech options legal in a given context — the menu the chooser presents. A PC actor (the
 * default) sees all four; an NPC actor (`npcActor`) is narrowed to Invade/Lock On (RAW's NPC Quick Tech
 * text); against a tech-immune target only the options `targetTechBlocked` doesn't reject survive
 * (Shut Down rejects all four; Biological rejects Invade/Bolster, leaving Scan/Lock On — matching
 * {@link QuickTechOption.allowedVsTechImmune}). Both filters compose. This is the shared legality seam
 * (mech menu + the NPC tech path).
 */
export function legalQuickTechOptions(ctx: QuickTechLegalityCtx = {}): readonly QuickTechOption[] {
  let options = QUICK_TECH_OPTIONS;
  if (ctx.npcActor) options = options.filter((o) => o.id === "invade" || o.id === "lock_on");
  if (ctx.targetTechBlocked) {
    const blocked = ctx.targetTechBlocked;
    options = options.filter((o) => !blocked(optionActionId(o)));
  }
  return options;
}

/** The actor/target binding a chosen Quick Tech option needs to build its intent. */
export type QuickTechIntentArgs = {
  /** The combatant taking the Quick Tech action (the activator / tech attacker). */
  actor: string;
  /** The combatant the option targets (an enemy for Invade/Lock On/Scan, an ally for Bolster). */
  target: string;
  /** Net accuracy (+) / difficulty (−) for the tech attack route (Invade); ignored by activate routes. */
  accuracy?: number;
  /** Recorded `choose` answers (e.g. `invade_option=fragment_signal` for Invade's FRAGMENT SIGNAL menu). */
  choices?: Record<string, string>;
};

/**
 * The menu router — map a chosen Quick Tech option to the intent its existing resolver consumes.
 * This is the load-bearing "routes each built-in to the correct resolver" seam: an `activate`
 * option becomes a `request_activate`, a `tech_attack` option becomes a tech `request_attack`.
 * Throws on an unknown option id (a programming error — the caller resolves the id off the table first).
 */
export function quickTechIntent(optionId: string, args: QuickTechIntentArgs): RequestActivate | RequestAttack {
  const option = quickTechOption(optionId);
  if (!option) throw new Error(`quickTechIntent: unknown Quick Tech option "${optionId}"`);
  const { actor, target, accuracy = 0, choices } = args;
  const hasChoices = choices !== undefined && Object.keys(choices).length > 0;
  if (option.route.kind === "tech_attack") {
    return {
      type: "request_attack",
      attacker: actor,
      target: { kind: "unit", id: target },
      accuracy,
      tech: true,
      weapon: option.route.weapon,
      ...(hasChoices ? { choices } : {}),
    };
  }
  return {
    type: "request_activate",
    actor,
    action: option.route.action,
    target,
    ...(hasChoices ? { choices } : {}),
  };
}

/**
 * One pick on a tech card, decoded. The browser carries a pick as a single id (an aim's
 * `optionId`, a Full Tech queue entry): a Quick Tech option id, a carried Quick Tech action id, or
 * a contributed invade option id (`<entityId>#<key>`, `gatherInvadeOptions`), which is an Invade
 * with that option pre-answered on the `invade_option` prompt.
 */
export function techMenuPick(pickId: string): { option: string; choices?: Record<string, string> } {
  return pickId.includes("#") ? { option: "invade", choices: { invade_option: pickId } } : { option: pickId };
}
